import { useMemo } from "react";
import type { ProtoFixture } from "@catbento/shared";
import { readSnapshot } from "../data/snapshot.js";
import {
  getAllMatchResults,
  getMatchResultCompetitions,
  getMatchResults,
  type CompetitionId,
  type LeagueChoice,
} from "../data/league.js";
import { LIVE } from "../live/flag.js";
import { liveApi } from "../live/api.js";
import { useLiveQuery } from "../live/query.js";
import { loadMatchResults } from "../live/results.js";
import { useLiveLeagueChoices } from "./useLeagueTable.js";

// Phase 02 seam: the Match Results widget's fixtures, by league id (ids-2). Track 2 (T2-2):
// with ?live=1 the widget's two hooks read app-api (/competitions + /seasons + /matches,
// live/results.ts) and adapt to the same shapes; without it, the snapshot, exactly as
// before. Team filtering stays renderer-side (Phase 04).

function useSnapshotMatchResultCompetitions(): LeagueChoice[] {
  return useMemo(() => getMatchResultCompetitions(readSnapshot()), []);
}

const NO_FIXTURES: ProtoFixture[] = [];

function useSnapshotMatchResults(competitionId: CompetitionId | null): ProtoFixture[] {
  return useMemo(
    () => (competitionId === null ? NO_FIXTURES : getMatchResults(readSnapshot(), competitionId)),
    [competitionId],
  );
}

function useLiveMatchResults(competitionId: CompetitionId | null): ProtoFixture[] {
  const league = useLiveLeagueChoices().find((c) => c.id === competitionId) ?? null;
  // An unknown id → no fixtures (the widget's own empty state), as the snapshot hook does.
  const key = league ? `results:${league.id}:newest` : null;
  return useLiveQuery(key, () => loadMatchResults(liveApi(), league as LeagueChoice)) ?? NO_FIXTURES;
}

/** The leagues that have match results (for the widget's selector). Live with ?live=1 —
 *  League Table's own list, so the Table ↔ Results switch always finds the league. */
export const useMatchResultCompetitions: () => LeagueChoice[] = LIVE
  ? useLiveLeagueChoices
  : useSnapshotMatchResultCompetitions;

/** A league's fixtures (flat), or [] if none captured. Live with ?live=1. */
export const useMatchResults: (competitionId: CompetitionId | null) => ProtoFixture[] = LIVE
  ? useLiveMatchResults
  : useSnapshotMatchResults;

/** Every competition's fixtures, keyed by competition (the Matches opposition lookup).
 *  Always the snapshot until the Matches slice — going live is per widget. */
export function useAllMatchResults(): Record<string, ProtoFixture[]> {
  return useMemo(() => getAllMatchResults(readSnapshot()), []);
}
